import React, { useState, useEffect, useRef } from 'react'
import { useFetchProducts } from 'hooks/useFetchProducts'
import { Helmet } from 'react-helmet-async'
import { Card } from 'ui/Card'
import { ProductsListSkeleton } from 'ui/ProductsListSkelton'
import { cacheProductSlugs } from 'utils/productSlugCache'

type ProductsListProps = {
  categoryId?: string | null
}

const ITEMS_PER_PAGE = 20

const ProductsList: React.FC<ProductsListProps> = ({ categoryId }) => {
  const { products, error, isError, isLoading, pagination } = useFetchProducts(
    categoryId,
    { page: 1, per_page: ITEMS_PER_PAGE },
  )
  const [allProducts, setAllProducts] = useState<typeof products>([])
  const [hasMore, setHasMore] = useState(true)
  const observerTarget = useRef<HTMLDivElement>(null)
  const previousCategoryId = useRef<string | null | undefined>(categoryId)

  // Reset when category changes
  useEffect(() => {
    if (previousCategoryId.current !== categoryId) {
      setAllProducts([])
      setHasMore(true)
      pagination.goToPage(1)
      previousCategoryId.current = categoryId
    }
  }, [categoryId, pagination])

  // Initialize products on first load or category change
  useEffect(() => {
    if (products && pagination.page === 1) {
      setAllProducts(products)
      setHasMore(products.length === ITEMS_PER_PAGE)
      cacheProductSlugs(products)
    }
  }, [products, pagination.page])

  // Append products when loading subsequent pages
  useEffect(() => {
    if (products && pagination.page > 1) {
      setAllProducts((prev) => {
        // Prevent duplicates
        const existingIds = new Set(prev?.map((p) => p.id) || [])
        const newProducts = products.filter((p) => !existingIds.has(p.id))
        return prev ? [...prev, ...newProducts] : products
      })
      setHasMore(products.length === ITEMS_PER_PAGE)
      cacheProductSlugs(products)
    }
  }, [products, pagination.page])

  // Setup Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          pagination.goToPage(pagination.page + 1)
        }
      },
      { threshold: 0.1 },
    )

    if (observerTarget.current) {
      observer.observe(observerTarget.current)
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current)
      }
    }
  }, [hasMore, isLoading, pagination])

  return (
    <div
      className='mt-8 p-4 rounded-md border'
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderColor: 'var(--border-color)',
      }}
    >
      <Helmet>
        <title className='text-xl  font-semibold'>
          بوک استوری - داستان کتاب
        </title>
        <meta
          name='description'
          content='بوک استوری - هر کتاب یک داستان، هر داستان یک زندگی دوباره'
        />
      </Helmet>
      {error && <h3 className='text-center my-4 w-full'>{error.message}</h3>}
      {isLoading && pagination.page === 1 && <ProductsListSkeleton />}

      <div className='grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mt-3'>
        {allProducts?.map((item) => (
          <Card
            key={item.id}
            id={item.id}
            title={item.title}
            author={item.attributes.author}
            imageUrl={item.images[0]}
            price={item.price}
            compareAtPrice={item.compareAtPrice}
          />
        ))}
      </div>

      {/* Sentinel element for infinite scroll */}
      {hasMore && (
        <div
          ref={observerTarget}
          className='h-10 w-full flex justify-center items-center mt-4'
        >
          {isLoading && pagination.page > 1 && (
            <div className='text-sm text-gray-500'>در حال بارگذاری...</div>
          )}
        </div>
      )}

      {/* Show "no more results" message */}
      {!hasMore && allProducts && allProducts.length > 0 && (
        <div className='text-center text-sm text-gray-500 mt-4'>
          همه محصولات نمایش داده شد
        </div>
      )}

      {/* Show "no results" message */}
      {!isLoading && allProducts && allProducts.length === 0 && (
        <div
          className='text-center text-base mt-8'
          style={{ color: 'var(--text-secondary)' }}
        >
          محصولی در این دسته‌بندی یافت نشد
        </div>
      )}
    </div>
  )
}

export default ProductsList
